/**
 * Authentication Zustand Store
 * 
 * Provides centralized authentication state management using Zustand.
 * Designed to work alongside React Query hooks for server state management.
 * 
 * Features:
 * - TypeScript-first with strict typing
 * - Persistent authentication state
 * - Integration with generated API hooks
 * - Sentry user context management
 * - Optimistic updates with server synchronization
 * 
 * @module TabPicker.AuthStore
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { setSentryUserContext } from '../utils/sentry';

/**
 * User Account Information
 * 
 * Represents an authenticated user in the TabPicker application.
 * Matches the interface used in AuthContext for compatibility.
 */
export interface User {
  /** Unique user ID from database */
  id: number;
  /** User's email address from Google OAuth */
  email: string;
  /** User's display name from Google OAuth */
  name: string;
  /** Google account ID for OAuth integration */
  googleId?: string;
}

/**
 * Authentication Store State
 * 
 * Defines the complete state shape for authentication management.
 */
interface AuthState {
  /** Currently authenticated user or null if not logged in */
  user: User | null;
  /** Whether authentication check is in progress */
  loading: boolean;
  /** Whether user data has been initialized from persistence */
  initialized: boolean;
}

/**
 * Authentication Store Actions
 * 
 * Defines all actions available for authentication state management.
 */
interface AuthActions {
  /** Set the authenticated user and update Sentry context */
  setUser: (user: User | null) => void;
  /** Set loading state for authentication operations */
  setLoading: (loading: boolean) => void;
  /** Log in a user with provided user data */
  login: (user: User) => void;
  /** Log out the current user and clear all state */
  logout: () => void;
  /** Initialize the store (called on app startup) */
  initialize: () => void;
  /** Reset the store to initial state */
  reset: () => void;
}

/**
 * Combined Authentication Store Type
 * 
 * Combines state and actions for the complete store interface.
 */
export type AuthStore = AuthState & AuthActions;

/**
 * Initial Authentication State
 * 
 * Default values for the authentication store.
 */
const initialState: AuthState = {
  user: null,
  loading: true,
  initialized: false,
};

/**
 * Authentication Zustand Store
 * 
 * Creates a persistent authentication store with Zustand.
 * Automatically syncs user state with Sentry for error tracking.
 * 
 * Storage Strategy:
 * - Persists user data to localStorage for session persistence
 * - Excludes loading and initialized flags from persistence
 * - Handles hydration gracefully with initialized flag
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setUser: (user: User | null) => {
        set({ user });
        
        // Update Sentry user context for error tracking
        if (user) {
          setSentryUserContext({
            id: user.id.toString(),
            email: user.email,
            name: user.name,
          });
        } else {
          setSentryUserContext(null);
        }
      },

      setLoading: (loading: boolean) => {
        set({ loading });
      },

      login: (user: User) => {
        const { setUser, setLoading } = get();
        setUser(user);
        setLoading(false);
      },

      logout: () => {
        const { setUser, setLoading } = get();
        setUser(null);
        setLoading(false);
        
        // Set flag to force re-authentication on next login
        try {
          sessionStorage.setItem('forceReauth', 'true');
        } catch (storageError) {
          console.warn('Failed to set forceReauth flag:', storageError);
        }
      },

      initialize: () => {
        set({ initialized: true, loading: false });
      },

      reset: () => {
        set(initialState);
        setSentryUserContext(null);
      },
    }),
    {
      name: 'auth-store',
      // Only persist user data, not loading states
      partialize: (state) => ({ 
        user: state.user 
      }),
      // Handle rehydration
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.initialized = true;
          state.loading = false;
        }
      },
    }
  )
);

/**
 * Authentication Store Selectors
 * 
 * Pre-defined selectors for common authentication state queries.
 * These can be used to optimize re-renders by subscribing only to specific state slices.
 */
export const authSelectors = {
  /** Select just the user object */
  user: (state: AuthStore) => state.user,
  /** Select authentication status */
  isAuthenticated: (state: AuthStore) => state.user !== null,
  /** Select loading state */
  isLoading: (state: AuthStore) => state.loading,
  /** Select initialization state */
  isInitialized: (state: AuthStore) => state.initialized,
  /** Select user ID (for convenience) */
  userId: (state: AuthStore) => state.user?.id,
  /** Select user email (for convenience) */
  userEmail: (state: AuthStore) => state.user?.email,
};

/**
 * Custom Hooks for Authentication Store
 * 
 * Convenience hooks that combine store access with common patterns.
 */

/**
 * Hook to get current user
 * 
 * @returns Current user object or null
 */
export const useCurrentUser = () => useAuthStore(authSelectors.user);

/**
 * Hook to check authentication status
 * 
 * @returns Boolean indicating if user is authenticated
 */
export const useIsAuthenticated = () => useAuthStore(authSelectors.isAuthenticated);

/**
 * Hook to get authentication loading state
 * 
 * @returns Boolean indicating if authentication is loading
 */
export const useAuthLoading = () => useAuthStore(authSelectors.isLoading);

/**
 * Hook to get authentication actions
 * 
 * @returns Object containing all authentication actions
 */
export const useAuthActions = () => useAuthStore((state) => ({
  setUser: state.setUser,
  setLoading: state.setLoading,
  login: state.login,
  logout: state.logout,
  initialize: state.initialize,
  reset: state.reset,
}));